import type { CSSProperties, ReactNode } from "react";
import { cn } from "#/lib/utils";

export interface SkeletonProps {
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({ className, style }: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={cn("animate-pulse rounded-full bg-lp-grey", className)}
      style={style}
    />
  );
}

export interface PillBtnProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: "green" | "dark" | "white" | "ghost";
  size?: "sm" | "md" | "lg";
  className?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  "aria-label"?: string;
}

const PILL_SIZES: Record<NonNullable<PillBtnProps["size"]>, string> = {
  sm: "h-11 px-7 text-base",
  md: "h-[61px] px-10 text-xl",
  lg: "h-[70px] px-12 text-[22px]",
};

const PILL_VARIANTS: Record<NonNullable<PillBtnProps["variant"]>, string> = {
  green: "bg-lp-green text-white hover:bg-lp-green-mid",
  dark: "bg-lp-green-dk text-white hover:bg-[#2f3c2b]",
  white:
    "bg-white text-lp-green-dk border-2 border-lp-green hover:bg-[#f0f5ed]",
  ghost: "bg-transparent text-lp-green-dk hover:bg-lp-green-soft",
};

export function PillBtn({
  children,
  onClick,
  variant = "green",
  size = "md",
  className = "",
  type = "button",
  disabled,
  "aria-label": ariaLabel,
}: PillBtnProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-full font-sans font-semibold transition disabled:opacity-50 ${PILL_SIZES[size]} ${PILL_VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function SectionHead({
  children,
  className = "",
  color = "text-lp-text",
}: {
  children: ReactNode;
  className?: string;
  color?: string;
}) {
  return (
    <h2
      className={`m-0 text-center font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight md:leading-none ${color} ${className}`}
    >
      {children}
    </h2>
  );
}

export interface EyebrowPillProps {
  children: ReactNode;
  variant?: "green" | "yellow";
  className?: string;
}

const EYEBROW_VARIANTS: Record<
  NonNullable<EyebrowPillProps["variant"]>,
  string
> = {
  green: "bg-lp-green text-white",
  yellow: "bg-lp-yellow text-lp-green-dk",
};

export function EyebrowPill({
  children,
  variant = "green",
  className = "",
}: EyebrowPillProps) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 font-display font-semibold text-xs uppercase tracking-[0.18em] ${EYEBROW_VARIANTS[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

export interface ArrowBtnProps {
  dir: "left" | "right";
  onClick?: () => void;
  size?: number;
  variant?: "light" | "filled";
  disabled?: boolean;
}

const ARROW_VARIANTS: Record<NonNullable<ArrowBtnProps["variant"]>, string> = {
  light: "bg-white text-lp-green",
  filled: "bg-lp-green text-white",
};

export function ArrowBtn({
  dir,
  onClick,
  size = 50,
  variant = "light",
  disabled,
}: ArrowBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir}
      disabled={disabled}
      className={`inline-flex cursor-pointer items-center justify-center rounded-full shadow-card transition hover:scale-105 disabled:cursor-default disabled:opacity-40 disabled:hover:scale-100 ${ARROW_VARIANTS[variant]}`}
      style={{ width: size, height: size }}
    >
      <svg
        aria-hidden="true"
        width="14"
        height="22"
        viewBox="0 0 15 25"
        fill="currentColor"
      >
        {dir === "left" ? (
          <path d="M 12.535 25.069 L 0 12.534 L 12.535 0 L 15 2.465 L 4.931 12.534 L 15 22.604 L 12.535 25.069 Z" />
        ) : (
          <path d="M 2.465 25.069 L 15 12.534 L 2.465 0 L 0 2.465 L 10.069 12.534 L 0 22.604 L 2.465 25.069 Z" />
        )}
      </svg>
    </button>
  );
}

export interface BookCoverProps {
  src: string | null;
  w: number;
  h: number;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  /**
   * "cover" crops the cover to fill the w×h box (default).
   * "contain" renders the whole cover at height h with width following the
   * image's natural ratio (w acts as a max width), so nothing is cropped and
   * no empty box shows around odd-shaped covers.
   */
  fit?: "cover" | "contain";
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
  srcSet?: string;
  sizes?: string;
}

export function BookCover({
  src,
  w,
  h,
  alt,
  className = "",
  style,
  fit = "cover",
  loading,
  fetchPriority,
  srcSet,
  sizes,
}: BookCoverProps) {
  if (fit === "contain") {
    if (!src) {
      return (
        <div
          className={cn("rounded bg-transparent", className)}
          style={{ width: w, height: h, ...style }}
        />
      );
    }
    return (
      <img
        src={src}
        srcSet={srcSet}
        sizes={srcSet ? sizes : undefined}
        alt={alt ?? ""}
        width={w || undefined}
        height={h || undefined}
        decoding="async"
        loading={loading}
        fetchPriority={fetchPriority}
        className={cn("block rounded object-contain shadow-book", className)}
        style={{
          height: "auto",
          width: "auto",
          maxHeight: h || undefined,
          maxWidth: w || undefined,
          ...style,
        }}
      />
    );
  }

  const labeled = alt ? { role: "img" as const, "aria-label": alt } : {};
  return (
    <div
      {...labeled}
      className={`overflow-hidden rounded bg-transparent shadow-book ${className}`}
      style={{
        width: w,
        height: h,
        backgroundImage: src ? `url(${src})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        ...style,
      }}
    />
  );
}

export function Star({
  filled = true,
  size = 18,
}: {
  filled?: boolean;
  size?: number;
}) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "rgb(237,191,28)" : "rgb(229,229,229)"}
    >
      <path d="m12 2 3 6.9 7.5.7-5.7 5 1.7 7.4L12 18.3 5.5 22l1.7-7.4-5.7-5 7.5-.7L12 2z" />
    </svg>
  );
}

export function Stars({
  rating = 5,
  size = 18,
}: {
  rating?: number;
  size?: number;
}) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} filled={i <= rating} size={size} />
      ))}
    </div>
  );
}

export function GreenRule({
  width = 1228,
  className = "",
}: {
  width?: number;
  className?: string;
}) {
  return (
    <div
      className={`mx-auto h-1 bg-lp-green-mid ${className}`}
      style={{ maxWidth: width, width: "100%" }}
    />
  );
}

export interface EggPillProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  radius?: number;
  clipRight?: boolean;
}

/**
 * The Figma "egg" / pill shape — soft-green oval/pill with optional flat right side.
 * `clipRight` produces the PDP variant where the pill bleeds to the viewport edge.
 */
export function EggPill({
  children,
  className = "",
  style,
  radius = 240,
  clipRight = false,
}: EggPillProps) {
  const computed: CSSProperties = clipRight
    ? {
        borderTopLeftRadius: radius,
        borderBottomLeftRadius: radius,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      }
    : { borderRadius: radius };
  return (
    <div
      className={`bg-lp-green-soft ${className}`}
      style={{ ...computed, ...style }}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <section className="bg-lp-green-soft pt-16 pb-14 text-center">
      <h1 className="m-0 mb-3 font-display font-bold text-5xl text-lp-text tracking-wide">
        {title.toUpperCase()}
      </h1>
      {subtitle && (
        <p className="m-0 mx-auto max-w-2xl px-6 font-display text-lg text-lp-text">
          {subtitle}
        </p>
      )}
    </section>
  );
}
